// useLeads.ts - React Query hooks for leads
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { notifications } from '@mantine/notifications';

export function useLeads(params?: any) {
  return useQuery({
    queryKey: ['leads', params],
    queryFn: () => api.leads.getAll(params),
  });
}

export function useLead(id: string) {
  return useQuery({
    queryKey: ['lead', id],
    queryFn: () => api.leads.getOne(id),
    enabled: !!id,
  });
}

export function useCreateLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => api.leads.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      notifications.show({
        title: 'Success',
        message: 'Lead created successfully',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to create lead',
        color: 'red',
      });
    },
  });
}

export function useUpdateLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      api.leads.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['lead', variables.id] });
      notifications.show({
        title: 'Success',
        message: 'Lead updated successfully',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to update lead',
        color: 'red',
      });
    },
  });
}

export function useDeleteLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.leads.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      notifications.show({
        title: 'Success',
        message: 'Lead deleted successfully',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to delete lead',
        color: 'red',
      });
    },
  });
}

export function useLeadMatching(id: string) {
  return useQuery({
    queryKey: ['lead-matches', id],
    queryFn: () => api.leads.match(id, { minMatchScore: 60 }),
    enabled: !!id,
  });
}

export function useBestMatch(id: string) {
  return useQuery({
    queryKey: ['best-match', id],
    queryFn: () => api.leads.getBestMatch(id),
    enabled: !!id,
  });
}

export function useDashboardAnalytics() {
  return useQuery({
    queryKey: ['dashboard-analytics'],
    queryFn: () => api.analytics.getDashboard(),
  });
}
